import { useEffect, useState, useRef, useCallback } from 'react';

export enum SCROLL_DIRECTION {
    LEFT = 'left',
    RIGHT = 'right',
}

export enum SCROLL_CONTROLS_MODE {
    LEFT = 'left',
    RIGHT = 'right',
    BOTH = 'both',
    NONE = 'none',
}

export interface ScrollTableEffectOptions<T> {
    entitiesToScroll: Array<T>;
    cellWidth: number;
    cellTotalWidth: number;
    scrollStep: number;
    shouldProcess: boolean;
}

export const useScrollTableEffect = <T extends any>(options: ScrollTableEffectOptions<T>) => {
    const { entitiesToScroll, cellWidth, cellTotalWidth, scrollStep, shouldProcess } = options;
    const [scrollControlsMode, setScrollControlsMode] = useState(SCROLL_CONTROLS_MODE.NONE);
    const [scrollOffsetSteps, setScrollOffsetSteps] = useState(0);
    const [containerWidth, setContainerWidth] = useState<number | null>(null);
    let tableBlockElem = useRef<HTMLDivElement | null>(null);

    const recountScrollControls = (entities: Array<T>) => {
        if (tableBlockElem.current && entities.length > 0) {
            const difference = getWidthDifference(entities);

            if (scrollOffsetSteps) {
                setScrollOffsetSteps(0);
            }

            // если не влезает элемент и если количество элементов меньше шага скролла
            // - не рисуем контролы скролла
            if (difference > 0 && entities.length > scrollStep) {
                setScrollControlsMode(SCROLL_CONTROLS_MODE.RIGHT);
            } else {
                removeScrollControls();
            }
        }
    };

    const recountContainerWidth = (enititesToScroll: Array<T>) => {
        if (tableBlockElem.current) {
            const originContainerClientWidth = tableBlockElem.current.clientWidth;
            const originContainerBoundingRectWidth = tableBlockElem.current.getBoundingClientRect().width;
            // иногда getBoundingClientRect считает не ту ширину; clientWidth вроде всегда выдает правильно
            // на всякий случай проверяем обе
            const originContainerWidth = Math.max(originContainerClientWidth, originContainerBoundingRectWidth);
            const requiredWidth = enititesToScroll.length * cellTotalWidth;

            if (requiredWidth > originContainerWidth) {
                const fittedCellsCount = Math.floor(originContainerWidth / cellTotalWidth);
                const margin = cellTotalWidth - cellWidth;
                // вычитаем 1 маргин, чтобы справа поджималось по ширине без последнего правого маргина
                const fittedWidth = fittedCellsCount * cellTotalWidth - margin;

                setContainerWidth(fittedWidth);
            } else {
                setContainerWidth(null);
            }
        }
    };

    useEffect(() => {
        const handlerWrapper = () => {
            if (shouldProcess) {
                setContainerWidth(null);
                recountScrollControls(entitiesToScroll);
                recountContainerWidth(entitiesToScroll);
            }
        };

        window.addEventListener('resize', handlerWrapper);

        return () => {
            window.removeEventListener('resize', handlerWrapper);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldProcess]);

    useEffect(() => {
        if (!shouldProcess) {
            removeScrollControls();
        }
    }, [shouldProcess]);

    useEffect(() => {
        if (shouldProcess) {
            recountScrollControls(entitiesToScroll);
            recountContainerWidth(entitiesToScroll);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entitiesToScroll, shouldProcess]);

    const scrollableContainerRef = useCallback(
        (node: HTMLDivElement | null) => {
            tableBlockElem.current = node;

            if (shouldProcess) {
                recountScrollControls(entitiesToScroll);
                recountContainerWidth(entitiesToScroll);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [entitiesToScroll, shouldProcess]
    );

    const removeScrollControls = () => {
        setScrollControlsMode(SCROLL_CONTROLS_MODE.NONE);
        setScrollOffsetSteps(0);
        setContainerWidth(null);
    };

    const getWidthDifference = (entities: Array<T>): number => {
        if (tableBlockElem.current && entities.length > 0) {
            const availableWidth = tableBlockElem.current.getBoundingClientRect().width;
            const requiredWidth = entities.length * cellTotalWidth;
            return requiredWidth - availableWidth;
        }

        return 0;
    };

    const scrollCards = (direction: SCROLL_DIRECTION) => {
        if (tableBlockElem.current) {
            const newValue = scrollOffsetSteps + (direction === SCROLL_DIRECTION.LEFT ? -1 : 1);
            const minValue = 0;
            const widthDifference = getWidthDifference(entitiesToScroll);
            const maxValue = Math.ceil(widthDifference / (cellTotalWidth * scrollStep));

            if (newValue > minValue && newValue < maxValue) {
                setScrollOffsetSteps(newValue);
                setScrollControlsMode(SCROLL_CONTROLS_MODE.BOTH);
            }
            if (newValue <= minValue) {
                setScrollOffsetSteps(minValue);
                setScrollControlsMode(SCROLL_CONTROLS_MODE.RIGHT);
            }
            if (newValue >= maxValue) {
                setScrollOffsetSteps(maxValue);
                setScrollControlsMode(SCROLL_CONTROLS_MODE.LEFT);
            }
        }
    };

    const getCellsOffset = (): number => {
        if (!shouldProcess) {
            return 0;
        }

        // допустимые значения - от 0 до (длина блока - отступ на 1 шаг)
        let cellOffset = 0;

        if (tableBlockElem.current && entitiesToScroll.length > scrollStep) {
            const targetCellsOffset = scrollOffsetSteps * cellTotalWidth * scrollStep;
            const maxContainerWidth = tableBlockElem.current.getBoundingClientRect().width;
            const maxRightOffset = Math.max(0, entitiesToScroll.length * cellTotalWidth - maxContainerWidth);
            const maxLeftOffset = 0;
            cellOffset = Math.min(Math.max(targetCellsOffset, maxLeftOffset), maxRightOffset);
        }

        return cellOffset;
    };

    return {
        scrollableContainerRef,
        scrollControlsMode,
        onControlClick: scrollCards,
        getCellsOffset,
        containerWidth,
    };
};
